import type { BiometryType } from '@stytch/react-native';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    FingerprintIcon,
    FullScreenSpinner,
    Icon,
    type IconType,
    ScanFaceIcon,
    ScreenContainer,
    Switch,
    useToastController,
    View,
    XStack,
} from 'smoothie';

import { useStytch } from '../../auth/hooks/useAuth';
import { captureException } from '../../logger/Logger';

const SecuritySettingsScreen = () => {
    const stytch = useStytch();
    const toast = useToastController();

    const [isLoading, setIsLoading] = useState(true);
    const [biometryType, setBiometryType] = useState<BiometryType | null>(null);
    const [biometryIcon, setBiometryIcon] = useState<IconType | null>(null);
    const [isBiometricsEnabled, setIsBiometricsEnabled] = useState(false);

    useEffect(() => {
        void (async () => {
            setIsLoading(true);
            try {
                const sensor = await stytch.biometrics.getSensor(true);
                setBiometryType(sensor.biometryType);
                setBiometryIcon(
                    sensor.biometryType === 'FaceID'
                        ? ScanFaceIcon
                        : FingerprintIcon,
                );

                const isRegistrationAvailable =
                    await stytch.biometrics.isRegistrationAvailable();
                setIsBiometricsEnabled(isRegistrationAvailable);
            } catch (e) {
                captureException(e);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [stytch]);

    const onUpdateBiometrics = async (enabled: boolean) => {
        setIsBiometricsEnabled(enabled);

        try {
            if (enabled) {
                await stytch.biometrics.register({
                    prompt: 'Register your biometric factor',
                    allowDeviceCredentials: true,
                    allowFallbackToCleartext: true,
                });
            } else {
                await stytch.biometrics.removeRegistration();
            }

            toast.show(`${biometryType} ${enabled ? '' : 'de'}activated!`, {
                duration: 2000,
                description: `You can ${
                    enabled ? 'now' : 'no longer'
                } use your biometrics`,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        {biometryIcon && <Icon icon={biometryIcon} />}
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show(
                `Error ${enabled ? '' : 'de'}activating ${biometryType}`,
                {
                    duration: 2000,
                },
            );
            setIsBiometricsEnabled(!enabled); // Revert flag if it didn't work
        }
    };

    if (isLoading) return <FullScreenSpinner />;

    return (
        <ScreenContainer title="Security" hasBackButton={true}>
            {biometryType == null ? (
                <BodyMediumRegular>
                    This device does not support biometrics.
                </BodyMediumRegular>
            ) : (
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    paddingBottom={20}
                    marginBottom={20}
                    borderBottomWidth={1}
                    borderBottomColor="$gray100"
                >
                    <XStack alignItems="center" gap={24} paddingLeft={12}>
                        {biometryIcon && <Icon icon={biometryIcon} />}
                        <BodyMediumRegular>{biometryType}</BodyMediumRegular>
                    </XStack>
                    <Switch
                        isChecked={isBiometricsEnabled}
                        onCheckedChange={onUpdateBiometrics}
                    />
                </XStack>
            )}
        </ScreenContainer>
    );
};

export default SecuritySettingsScreen;
